import type { Status } from '../../shared/pruefpunkte.js';
import { modus } from '../api.js';
import { abschnitt, h, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { ergebnisVon, melde } from '../ergebnisse.js';
import { istAndroid, istIos } from '../geraet.js';
import { halteZurueck, type Ansicht } from '../router.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

/**
 * Zurück-Geste (Konzept, Bedienung: „Zurück und Tastatur wie erwartet“): Wischen vom
 * linken Rand am iPhone und die Zurück-Geste am Android sollen zuerst einen offenen
 * Dialog schließen, statt die Seite zu verlassen. Der Dialog legt dafür beim Öffnen
 * einen eigenen Eintrag in der History an; die Geste nimmt ihn weg (popstate) und
 * schließt so den Dialog. Chrome am Android kann einen modalen Dialog bei der Geste
 * auch selbst schließen (CloseWatcher); dann räumt die Ansicht ihren Eintrag weg.
 */

/**
 * Wie der Dialog zuging: über seinen History-Eintrag, vom Browser selbst, über den Knopf, mit Escape
 * (Tastatur am Tablet, keine Geste) oder beim Verlassen der Seite.
 */
type Grund = 'popstate' | 'abbruch' | 'knopf' | 'escape' | 'verlassen';
type Beobachtung = 'nur-dialog' | 'mit-wischen' | 'seite' | 'nichts';

interface DialogEintrag {
  tfmDialog: string;
}

function gestenText(): string {
  if (istIos()) return 'Vom linken Bildschirmrand nach rechts wischen.';
  if (istAndroid()) return 'Die Zurück-Geste machen (vom Rand zur Mitte wischen) oder die Zurück-Taste drücken.';
  return 'Die Zurück-Geste des Geräts machen.';
}

export const zurueck: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const pfad = location.pathname;
  /** Kennung dieses Aufrufs: Ein alter Dialog-Eintrag aus einem früheren Besuch zählt nicht als eigener. */
  const kennung = crypto.randomUUID();
  const status = meldung();
  const statusFrage = meldung();
  const closeWatcher = 'CloseWatcher' in window;
  const navigationApi = 'navigation' in window;
  let geoeffnetAm = 0;
  let grund: Grund | undefined;
  /** Die nächste popstate kommt vom eigenen history.back(), mit dem die Ansicht ihren Eintrag wegräumt. */
  let eigenesZurueck = false;

  const istEigenerEintrag = (zustand: unknown): boolean =>
    typeof zustand === 'object' && zustand !== null && (zustand as Partial<DialogEintrag>).tfmDialog === kennung;

  const details = (ergebnis: string) => ({ ergebnis, offenMs: Date.now() - geoeffnetAm, closeWatcher, navigationApi, modus: modus() });

  const dialog = h(
    'dialog',
    { class: 'testdialog', 'aria-labelledby': 'testdialog-titel', 'aria-describedby': 'testdialog-text' },
    h('h2', { id: 'testdialog-titel' }, 'Testdialog'),
    h('p', { id: 'testdialog-text' }, gestenText()),
    h('p', { class: 'hilfe' }, 'Erwartet: Nur dieser Dialog geht zu, die Seite dahinter bleibt.'),
    knopf('Schließen', () => schliesse('knopf'), 'sekundaer'),
  );
  const unterstuetzt = typeof dialog.showModal === 'function';

  function schliesse(g: Grund): void {
    if (!dialog.open) return;
    grund = g;
    dialog.close();
  }

  const oeffne = async (): Promise<void> => {
    if (dialog.open) return;
    if (!unterstuetzt) {
      await melde('zurueck-dialog', 'nicht_unterstuetzt', 'Der Browser kennt keinen modalen Dialog (showModal fehlt).', { modus: modus() });
      return;
    }
    grund = undefined;
    dialog.showModal();
    // Ohne Anker (#…): Sonst springt der Browser beim Zurück zum Anker und nimmt dem Knopf den Fokus.
    const adresse = location.pathname + location.search;
    if (location.hash) history.replaceState(history.state, '', adresse);
    history.pushState({ tfmDialog: kennung } satisfies DialogEintrag, '', adresse);
    geoeffnetAm = Date.now();
    setzeMeldung(status, '');
  };

  const meldeZu = async (g: 'popstate' | 'abbruch'): Promise<void> => {
    await melde(
      'zurueck-dialog',
      'ok',
      g === 'popstate'
        ? 'Zurück schloss den Dialog über seinen History-Eintrag (popstate); die Seite blieb.'
        : 'Der Browser schloss den Dialog bei Zurück selbst (Abbruch des Dialogs, ohne History); die Seite blieb.',
      details(g),
    );
    setzeMeldung(status, 'Gemessen: Der Dialog ging zu, die Seite blieb. Jetzt unten die Frage beantworten.', 'ok');
  };

  // Escape kommt von einer angeschlossenen Tastatur und schließt den Dialog wie die Geste; zählen soll nur die Geste.
  aufraeumer.hoere(dialog, 'keydown', (ereignis) => {
    if ((ereignis as KeyboardEvent).key === 'Escape') grund ??= 'escape';
  });
  aufraeumer.hoere(dialog, 'close', () => {
    // Ohne eigenen Grund hat der Browser den Dialog geschlossen; am Handy ohne Escape-Taste heißt das: bei der Geste.
    const g = grund ?? 'abbruch';
    grund = undefined;
    if ((g === 'abbruch' || g === 'knopf' || g === 'escape') && istEigenerEintrag(history.state)) {
      eigenesZurueck = true;
      history.back();
    }
    if (g === 'popstate' || g === 'abbruch') void meldeZu(g);
    else if (g === 'knopf' || g === 'escape') {
      setzeMeldung(status, `Mit ${g === 'knopf' ? 'dem Knopf' : 'Escape'} geschlossen; das zählt nicht. Bitte erneut öffnen und mit der Zurück-Geste schließen.`);
    }
  });

  aufraeumer.dazu(
    halteZurueck((ereignis) => {
      const hierGeblieben = location.pathname === pfad;
      if (eigenesZurueck) {
        eigenesZurueck = false;
        return hierGeblieben;
      }
      if (!dialog.open) {
        // Vorwärts in einen alten Dialog-Eintrag: Die Seite bleibt, der Dialog geht nicht wieder auf.
        return hierGeblieben && istEigenerEintrag(ereignis.state);
      }
      if (hierGeblieben) {
        if (!istEigenerEintrag(ereignis.state)) schliesse('popstate');
        return true;
      }
      // Die Navigation geht über den Dialog-Eintrag hinaus: Die Seite wechselt, obwohl der Dialog offen ist.
      void melde('zurueck-dialog', 'fehler', 'Zurück verließ die Seite, obwohl der Dialog offen war.', details('seite-verlassen'));
      schliesse('verlassen');
      return false;
    }),
  );
  aufraeumer.dazu(() => schliesse('verlassen'));

  const antworte = async (beobachtung: Beobachtung): Promise<void> => {
    const gemessen = ergebnisVon('zurueck-dialog');
    const antwortDetails = {
      beobachtet: beobachtung,
      gemessen: gemessen?.modus === modus() ? (gemessen.details?.ergebnis ?? gemessen.status) : null,
      modus: modus(),
      quelle: 'von Hand',
    };
    const [ergebnis, text]: [Status, string] =
      beobachtung === 'nur-dialog'
        ? ['ok', 'Nur der Dialog ging zu, die Seite blieb (laut Angabe).']
        : beobachtung === 'mit-wischen'
          ? ['ok', 'Der Dialog ging zu und die Seite blieb, schob sich dabei aber kurz zur Seite (laut Angabe).']
          : beobachtung === 'seite'
            ? ['fehler', 'Die Geste verließ die Seite oder schloss die App, statt nur den Dialog zu schließen (laut Angabe).']
            : ['nicht_unterstuetzt', 'Die Geste bewirkte nichts (laut Angabe). Dialoge brauchen dann immer einen sichtbaren Schließen-Knopf.'];
    await melde('zurueck-beobachtung', ergebnis, text, antwortDetails);
    setzeMeldung(statusFrage, 'Gespeichert.', 'ok');
  };

  ziel.append(
    ...kopf('Zurück-Geste', 'Schließt die Zurück-Geste zuerst einen offenen Dialog, statt die Seite zu verlassen? (Konzept, Bedienung)'),
    abschnitt(
      'Dialog',
      pruefpunktStand('zurueck-dialog', aufraeumer),
      schritte(
        'In der installierten App prüfen; danach gern auch im Browser.',
        '„Dialog öffnen“ antippen.',
        gestenText(),
        'Erwartet: Nur der Dialog geht zu, diese Seite bleibt. Ging die Seite weg oder die App zu, wieder hierher zurückkehren.',
        'Unten die Frage beantworten, auch wenn nichts geschah.',
      ),
      h('div', { class: 'knopfreihe' }, knopf('Dialog öffnen', () => oeffne())),
      status,
    ),
    abschnitt(
      'Frage',
      pruefpunktStand('zurueck-beobachtung', aufraeumer),
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-zurueck' },
        h('p', { id: 'frage-zurueck' }, 'Was geschah bei der Zurück-Geste?'),
        knopf('Nur der Dialog ging zu', () => antworte('nur-dialog')),
        knopf('Dialog zu, Seite schob sich kurz weg', () => antworte('mit-wischen'), 'sekundaer'),
        knopf('Seite gewechselt oder App zu', () => antworte('seite'), 'sekundaer'),
        knopf('Nichts geschah', () => antworte('nichts'), 'sekundaer'),
      ),
      statusFrage,
    ),
    abschnitt(
      'Was der Browser meldet',
      liste(
        zeile('Modaler Dialog (showModal)', unterstuetzt ? 'ja' : 'nein'),
        zeile('CloseWatcher (Browser schließt Dialoge bei Zurück selbst)', closeWatcher ? 'ja' : 'nein'),
        zeile('Navigation API', navigationApi ? 'ja' : 'nein'),
        zeile('Läuft als', modus() === 'app' ? 'installierte App' : 'Browser'),
      ),
    ),
    dialog,
  );
  return aufraeumer.alles;
};
